import { z } from "zod/v4";

import { MAILING_LIST_DOMAINS } from "api/memberPortal/mailList/mailList.types";
import { createZodEnums } from "components/Forms/utils/validation.utils";

// Local-part (before the @) charset per RFC 5321, common subset. The actual
// domain is appended and validated on the backend against the environment's
// configured domain, so the frontend only checks the prefix is well-formed.
const EMAIL_PREFIX_PATTERN = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+$/;

export const createMailListSchema = z.object({
  name: z.string().min(1, { message: "List name is required" }),
  category: z.number(),
  email: z
    .string()
    .min(1, { message: "Email prefix is required" })
    .refine((val) => EMAIL_PREFIX_PATTERN.test(val), {
      message: "Must be a valid email prefix",
    }),
  description: z.string().min(1, { message: "Description is required" }),
  domain: createZodEnums({
    valueObject: MAILING_LIST_DOMAINS,
    customErrorText: "Domain is required",
  }),
});

export type CreateMailListFormValues = z.infer<typeof createMailListSchema>;

// Edit schema (MailListDetailsCard). Here `email` holds the full address (the
// list already exists), and the card doesn't let the user change the email or
// domain — they're submitted unchanged. So email is validated as a complete
// address rather than a prefix. Domain suffix consistency is enforced by the
// backend on every write.
export const editMailListSchema = z.object({
  name: z.string().min(1, { message: "List name is required" }),
  category: z.number(),
  email: z.email({ message: "Must be a valid email address" }),
  description: z.string().min(1, { message: "Description is required" }),
  domain: createZodEnums({
    valueObject: MAILING_LIST_DOMAINS,
    customErrorText: "Domain is required",
  }),
});

export const defaultMailListValues = {
  name: "",
  category: 1,
  email: "",
  domain: MAILING_LIST_DOMAINS.NIAP,
  description: "",
} as CreateMailListFormValues;

export const mailListFilterSchema = z.object({
  sort: z.string(),
  domain: z.string(),
  search: z.string(),
});

export type MailListFilterValues = z.infer<typeof mailListFilterSchema>;

// Empty domain = "All" (no domain filter). Defaulting to a specific domain
// would silently hide every other domain's lists on first load.
export const defaultMailListFilterValues: MailListFilterValues = {
  sort: "-id",
  domain: "",
  search: "",
};

export const MAILING_LIST_SORT_OPTIONS = [
  {
    label: "Recently Created",
    value: "-id",
  },
  {
    label: "Name (A-Z)",
    value: "name",
  },
  {
    label: "Name (Z-A)",
    value: "-name",
  },
  {
    label: "Member Count",
    value: "-member_count",
  },
];
